/**
 * PartnerPackBanner.jsx
 *
 * Quiet HomePage card that surfaces:
 *   • "Walk the Partner Pack" — for users who haven't activated yet
 *     (shows progress: "2 of 5 modules walked")
 *   • "Annual re-affirmation due" — for activated partners after 365 days
 *   • "Open your partner dashboard" — for activated, current partners
 *
 * Mirrors the aesthetic of TeamAlignmentBanner — gold-mist border, italic
 * Cormorant body, subtle gold pulse. Silent (returns null) for
 * unauthenticated visitors or while loading.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Crown, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PartnerPackBanner() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("reclaim_token");
    if (!token) return undefined;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/partner-pack/state`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setState(r.data);
      } catch {
        if (!cancelled) setState(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!state) return null;

  // Build the message according to user's state.
  const isPartner = state.is_partner;
  const reaffirm = state.reaffirmation_due;
  const modulesDone = state.modules_done;
  const modulesTotal = state.modules_total;

  let kicker, headline, sub, icon, glow;
  if (reaffirm) {
    kicker = "Annual re-affirmation due";
    headline = "Re-affirm the three commitments.";
    sub = "A short re-walk · about a minute.";
    icon = <Sparkles size={14} style={{ color: "#F3EDDD" }} />;
    glow = true;
  } else if (isPartner) {
    kicker = "You are a partner";
    headline = "Open your dashboard.";
    sub = `${state.commission_summary.headline} Track your three-tier referrals.`;
    icon = <Crown size={14} style={{ color: "#F3EDDD" }} />;
    glow = false;
  } else if (modulesDone > 0) {
    kicker = "Continue the Partner Pack";
    headline = `${modulesDone} of ${modulesTotal} walked.`;
    sub = "Pick up where you left off.";
    icon = <Sparkles size={14} style={{ color: "#F3EDDD" }} />;
    glow = true;
  } else {
    kicker = "Become a Partner";
    headline = "Walk it. Then share it.";
    sub = `${state.commission_summary.headline} Five short transmissions + activation.`;
    icon = <Sparkles size={14} style={{ color: "#F3EDDD" }} />;
    glow = true;
  }

  const targetRoute = isPartner && !reaffirm ? "/affiliate" : "/partner-pack";

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      onClick={() => navigate(targetRoute)}
      className="w-full rounded-2xl p-5 sm:p-6 mb-6 text-left flex items-center gap-4"
      style={{
        background: glow
          ? "radial-gradient(ellipse at 0% 50%, rgba(245,224,173,0.30) 0%, rgba(7,13,11,0.92) 75%)"
          : "radial-gradient(ellipse at 0% 50%, rgba(215,187,122,0.22) 0%, rgba(7,13,11,0.88) 70%)",
        border: `1px solid ${glow ? "rgba(215,187,122,0.55)" : "rgba(215,187,122,0.4)"}`,
        boxShadow: glow ? "0 0 30px rgba(245,224,173,0.18)" : "0 0 16px rgba(215,187,122,0.10)",
      }}
      data-testid="home-partner-pack-banner"
    >
      <motion.div
        animate={glow ? { scale: [1, 1.06, 1], opacity: [0.85, 1, 0.85] } : {}}
        transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
        className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
        style={{
          background: glow ? "rgba(245,224,173,0.22)" : "rgba(215,187,122,0.10)",
          border: `1px solid ${glow ? "#A88A4C" : "rgba(215,187,122,0.32)"}`,
        }}
      >
        {icon}
      </motion.div>
      <div className="flex-1 min-w-0">
        <p
          className="uppercase tracking-[0.32em] mb-1"
          style={{ color: "#D7BB7A", fontFamily: '"Manrope", sans-serif', fontSize: "0.56rem" }}
          data-testid="home-partner-kicker"
        >
          {kicker}
        </p>
        <p
          className="italic mb-0.5"
          style={{ color: "#F3EDDD", fontFamily: '"Cormorant Garamond", serif', fontSize: "1.2rem", lineHeight: 1.3 }}
          data-testid="home-partner-headline"
        >
          {headline}
        </p>
        <p
          className="italic"
          style={{ color: "rgba(243, 237, 221, 0.66)", fontFamily: '"Cormorant Garamond", serif', fontSize: "0.92rem", lineHeight: 1.4 }}
        >
          {sub}
        </p>
      </div>
      <ArrowRight size={14} style={{ color: "#D7BB7A" }} />
    </motion.button>
  );
}
